/**
 * Search Configuration Provider
 * Allows plugins to register custom entity types and sample queries
 * Dynamically fetches entity types from the API based on current project
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { getAPIClient } from '../../api/client';
import { useQuery } from '@tanstack/react-query';
import { getPolicyPack } from '../../api/policy';
import { getProject } from '../../api/projects';
import { getQuerySamplesFromPolicyPack, getEntityTypesFromPolicyPack } from '../../utils/policyPackLoader';
import { KEYSTONE_RFP_ENTITY_MAPPINGS, getEntityTypeMapping } from '../../utils/keystoneRfpQueryRouter';

export interface EntityTypeConfig {
  id: string;
  label: string;
  entityType: string;
  keywords: string[];
  category?: string;
  enabled: boolean;
}

export interface SearchConfig {
  entityTypes: EntityTypeConfig[];
  sampleQueries: string[];
}

interface SearchConfigContextType {
  config: SearchConfig;
  registerEntityTypes: (types: EntityTypeConfig[]) => void;
  registerSampleQueries: (queries: string[]) => void;
  resetToDefaults: () => void;
  fetchEntityTypesIfNeeded: () => Promise<void>;
}

// Default entity types that work across many projects
const DEFAULT_ENTITY_TYPES: EntityTypeConfig[] = [
  {
    id: 'alert',
    label: 'Alerts',
    entityType: 'alert',
    keywords: ['alert', 'notification', 'warning', 'critical', 'issue', 'violation', 'breach'],
    category: 'Safety',
    enabled: true,
  },
  {
    id: 'unified_alert',
    label: 'Unified Alerts',
    entityType: 'unified_alert',
    keywords: ['alert', 'notification', 'warning', 'critical', 'issue', 'violation', 'breach'],
    category: 'Safety',
    enabled: true,
  },
  {
    id: 'document',
    label: 'Documents',
    entityType: 'document',
    keywords: ['document', 'file', 'report', 'certificate', 'record', 'pdf'],
    category: 'Documents',
    enabled: true,
  },
  {
    id: 'document_registry',
    label: 'Document Registry',
    entityType: 'document_registry',
    keywords: ['document', 'file', 'report', 'certificate', 'record', 'artifact', 'pdf', 'attachment'],
    category: 'Documents',
    enabled: true,
  },
  {
    id: 'compliance',
    label: 'Compliance',
    entityType: 'compliance',
    keywords: ['compliance', 'deadline', 'requirement', 'regulation', 'permit'],
    category: 'Compliance',
    enabled: true,
  },
  {
    id: 'incident',
    label: 'Incidents',
    entityType: 'incident',
    keywords: ['incident', 'accident', 'event', 'safety', 'issue', 'problem'],
    category: 'Safety',
    enabled: true,
  },
  {
    id: 'inspection',
    label: 'Inspections',
    entityType: 'inspection',
    keywords: ['inspection', 'audit', 'check', 'verification', 'assessment'],
    category: 'Compliance',
    enabled: true,
  },
];

const DEFAULT_SAMPLE_QUERIES = [
  'Show me all critical alerts',
  'What compliance deadlines are upcoming?',
  'List recent incidents',
  'Show me documents from last month',
  'What inspections are pending?',
  'Summarize current issues',
];

const SearchConfigContext = createContext<SearchConfigContextType | undefined>(undefined);

// Cache entity type checks per project to avoid repeated queries
const entityTypeCache = new Map<string, EntityTypeConfig[]>();
const fetchingProjects = new Set<string>();

export function SearchConfigProvider({ children }: { children: ReactNode }) {
  const { projectId } = useWorkspace();
  const [config, setConfig] = useState<SearchConfig>({
    entityTypes: DEFAULT_ENTITY_TYPES,
    sampleQueries: DEFAULT_SAMPLE_QUERIES,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Load project to get policy pack ID
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
  });

  // Load policy pack if project has one
  const { data: policyPack } = useQuery({
    queryKey: ['policy-pack', project?.policy_pack_id],
    queryFn: () => getPolicyPack(project!.policy_pack_id!),
    enabled: !!project?.policy_pack_id,
  });

  // Extract sample queries and entity types from policy pack
  useEffect(() => {
    if (policyPack) {
      // Policy pack can have data in rules field (YAML is parsed into rules)
      const policyData = policyPack.rules || policyPack;
      
      // Extract query samples from policy pack
      const querySamples = getQuerySamplesFromPolicyPack(policyData);
      
      // Extract entity types from policy pack
      const policyEntityTypes = getEntityTypesFromPolicyPack(policyData);
      
      if (querySamples.length > 0) {
        console.log('[SearchConfigProvider] Loaded sample queries from policy pack:', querySamples.length);
        setConfig(prev => ({
          ...prev,
          sampleQueries: querySamples,
        }));
      } else {
        console.log('[SearchConfigProvider] No query samples found in policy pack, using defaults');
        setConfig(prev => ({
          ...prev,
          sampleQueries: DEFAULT_SAMPLE_QUERIES,
        }));
      }
      
      // Use policy pack entity types if available, otherwise fallback to default entity types
      if (policyEntityTypes.length > 0) {
        console.log('[SearchConfigProvider] Loaded entity types from policy pack:', policyEntityTypes.length);
        console.log('[SearchConfigProvider] Entity types:', policyEntityTypes.map(et => et.label).join(', '));
        
        // Enrich with keyword mappings if Keystone RFP entity types
        const enrichedTypes = policyEntityTypes.map(et => {
          const mapping = getEntityTypeMapping(et.entityType);
          if (mapping) {
            console.log(`[SearchConfigProvider] Enriching ${et.entityType} with ${mapping.keywords.length} keywords`);
            return {
              ...et,
              keywords: mapping.keywords,
              category: mapping.category,
            };
          }
          return et;
        });
        
        setConfig(prev => ({
          ...prev,
          entityTypes: enrichedTypes,
        }));
        
        // Cache policy pack entity types for this project
        if (projectId) {
          entityTypeCache.set(projectId, enrichedTypes);
        }
      } else {
        console.log('[SearchConfigProvider] No entity types found in policy pack, using defaults (dynamic detection available via fetchEntityTypesIfNeeded)');
        // Fallback to defaults - dynamic detection can be triggered when search page is accessed
        setConfig(prev => ({
          ...prev,
          entityTypes: DEFAULT_ENTITY_TYPES,
        }));
      }
    } else if (project && !project.policy_pack_id) {
      // No policy pack assigned, use defaults
      console.log('[SearchConfigProvider] No policy pack assigned to project, using default queries and entity types');
      setConfig(prev => ({
        ...prev,
        sampleQueries: DEFAULT_SAMPLE_QUERIES,
        entityTypes: DEFAULT_ENTITY_TYPES,
      }));
    } else if (!projectId) {
      // No project selected, use defaults
      setConfig(prev => ({
        ...prev,
        sampleQueries: DEFAULT_SAMPLE_QUERIES,
        entityTypes: DEFAULT_ENTITY_TYPES,
      }));
    }
  }, [policyPack, project, projectId]);

  // Lazy fetch entity types - only when search is actually used
  // Don't fetch on mount - wait until useSearchConfig is called or search page is accessed
  const fetchEntityTypesIfNeeded = useCallback(async () => {
    if (!projectId) {
      return;
    }

    // Check cache first
    if (entityTypeCache.has(projectId)) {
      const cachedTypes = entityTypeCache.get(projectId)!;
      setConfig(prev => ({
        ...prev,
        entityTypes: cachedTypes,
      }));
      return;
    }

    // Prevent duplicate fetches for the same project
    if (fetchingProjects.has(projectId)) {
      return;
    }

    fetchingProjects.add(projectId);
    setIsLoading(true);
    
    try {
      const apiClient = getAPIClient();
      
      // Get distinct entity types by querying with a high limit and extracting unique types
      // We'll query a few common entity types to see what exists, then build the config
      // DISABLED: Legacy entity type checking (makes 20 individual API calls)
      // Now we get entity types from the policy pack instead - much more efficient!
      // 
      // const commonTypes = [
      //   'alert', 'unified_alert', 'document', 'document_registry', 'compliance', 
      //   'incident', 'inspection', 'field_parameter_measurement', 'pfas_concentration_result',
      //   'monitoring_well', 'remediation_activity', 'la_case', 'land_asset', 'sensor_reading',
      //   'equipment', 'production_record', 'stockpile', 'mining_plan', 'court_case'
      // ];
      // 
      // const availableTypes: EntityTypeConfig[] = [];
      // 
      // // Check which entity types actually exist in this project
      // // Use Promise.all with limited concurrency to avoid overwhelming the server
      // const checkPromises = commonTypes.map(async (entityType) => {
      //   try {
      //     const response = await apiClient.post('/entities/query', {
      //       project_id: projectId,
      //       entity_type: entityType,
      //       limit: 1, // Just check if any exist
      //     });
      //     
      //     if (response.entities && response.entities.length > 0) {
      //       // Find matching default config or create a basic one
      //       const defaultConfig = DEFAULT_ENTITY_TYPES.find(t => t.entityType === entityType);
      //       if (defaultConfig) {
      //         return defaultConfig;
      //       } else {
      //         // Create a basic config for this entity type
      //         return {
      //           id: entityType,
      //           label: entityType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      //           entityType: entityType,
      //           keywords: [entityType.replace(/_/g, ' ')],
      //           enabled: true,
      //         };
      //       }
      //     }
      //     return null;
      //   } catch (error) {
      //     // Entity type doesn't exist or error - skip it
      //     return null;
      //   }
      // });
      // 
      // const results = await Promise.all(checkPromises);
      // const validTypes = results.filter((t): t is EntityTypeConfig => t !== null);
      // availableTypes.push(...validTypes);
      // 
      // // Cache the results
      // entityTypeCache.set(projectId, availableTypes);
      // 
      // // If we found entity types, use them; otherwise fall back to defaults
      // if (availableTypes.length > 0) {
      //   console.log(`[SearchConfigProvider] Found ${availableTypes.length} entity types for project ${projectId}`);
      //   setConfig(prev => ({
      //     ...prev,
      //     entityTypes: availableTypes,
      //   }));
      // } else {
      //   console.log(`[SearchConfigProvider] No entity types found, using defaults`);
      // }
      
      console.log(`[SearchConfigProvider] Skipping legacy entity type checks - using policy pack entity types instead`);
    } catch (error) {
      console.error('[SearchConfigProvider] Error fetching entity types:', error);
      // Keep defaults on error
    } finally {
      fetchingProjects.delete(projectId);
      setIsLoading(false);
    }
  }, [projectId]);

  // Only reset config when project changes, don't fetch automatically
  useEffect(() => {
    if (!projectId) {
      // Reset to defaults if no project
      setConfig({
        entityTypes: DEFAULT_ENTITY_TYPES,
        sampleQueries: DEFAULT_SAMPLE_QUERIES,
      });
      return;
    }

    // Check cache and set if available, but don't fetch
    if (entityTypeCache.has(projectId)) {
      const cachedTypes = entityTypeCache.get(projectId)!;
      setConfig(prev => ({
        ...prev,
        entityTypes: cachedTypes,
      }));
    }
  }, [projectId]);

  // Listen for app config queries and entity types being set
  useEffect(() => {
    const checkForConfig = () => {
      const configQueries = (window as any).__tracekit_search_queries;
      const configEntityTypes = (window as any).__tracekit_entity_types;
      
      if (configQueries && Array.isArray(configQueries) && configQueries.length > 0) {
        console.log('[SearchConfigProvider] Loading sample queries from config:', configQueries);
        setConfig(prev => ({
          ...prev,
          sampleQueries: configQueries,
        }));
      }
      
      if (configEntityTypes && Array.isArray(configEntityTypes) && configEntityTypes.length > 0) {
        console.log('[SearchConfigProvider] Loading entity types from config:', configEntityTypes);
        setConfig(prev => ({
          ...prev,
          entityTypes: configEntityTypes,
        }));
      }
    };

    // Check immediately
    checkForConfig();
    
    // Check again after a brief delay to catch async config loading
    const timeout = setTimeout(checkForConfig, 100);
    
    // Listen for config ready event
    const handleConfigReady = () => {
      checkForConfig();
    };
    window.addEventListener('tracekitConfigReady', handleConfigReady);
    
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('tracekitConfigReady', handleConfigReady);
    };
  }, []);

  const registerEntityTypes = useCallback((types: EntityTypeConfig[]) => {
    console.log('[SearchConfigProvider] Registering entity types:', types.length);
    setConfig((prev) => ({
      ...prev,
      entityTypes: [...types], // Replace with project-specific types
    }));
  }, []);

  const registerSampleQueries = useCallback((queries: string[]) => {
    console.log('[SearchConfigProvider] Registering sample queries:', queries);
    setConfig((prev) => ({
      ...prev,
      sampleQueries: queries,
    }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setConfig({
      entityTypes: DEFAULT_ENTITY_TYPES,
      sampleQueries: DEFAULT_SAMPLE_QUERIES,
    });
  }, []);

  return (
    <SearchConfigContext.Provider value={{ config, registerEntityTypes, registerSampleQueries, resetToDefaults, fetchEntityTypesIfNeeded }}>
      {children}
    </SearchConfigContext.Provider>
  );
}

export function useSearchConfig() {
  const context = useContext(SearchConfigContext);
  if (!context) {
    throw new Error('useSearchConfig must be used within SearchConfigProvider');
  }
  return context;
}


